import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { Blockquote, Button, Spinner } from '@ui';
import Joi from 'joi';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from 'redux/store';
import { Forgot } from '~/components/auth/Forgot';
import useModalManager from '~/hooks/useModalManager';
import FormInput from './FormInput';

interface Props {
    mandatoryUserAccount?: boolean;
    debouncedOnChange?: (email: string) => void;
    nameMapping?: {
        name?: string;
        email?: string;
        password?: string;
        password_confirmation?: string;
    };
    onUserFound?: (found: boolean) => void;
    // when set (kiosk manual login), exchange email + password for a token instead of
    // only guessing the account, so downstream requests run authenticated
    onAuthenticate?: (token: string, user: any) => void;
    // hands the login trigger up so the parent can fire it from the "Weiter" step transition
    registerAuthenticate?: (fn: (() => boolean) | null) => void;
    // reports the in-flight login state so the parent can show a loading "Weiter" button
    onAuthenticatingChange?: (loading: boolean) => void;
}

export const InFormLogin = ({
    debouncedOnChange,
    mandatoryUserAccount,
    nameMapping,
    onUserFound,
    onAuthenticate,
    registerAuthenticate,
    onAuthenticatingChange
}: Props) => {
    const api = useApi();
    const { t } = useTranslation('auth');
    const { getValues, watch, setFocus, setValue } = useFormContext();
    const { isAuthenticated } = useSelector((state: AppState) => state.auth);
    const createAccount = watch('create_account', Boolean(mandatoryUserAccount));
    const [userFound, setUserFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const { addModal, removeModal } = useModalManager();

    useEffect(() => {
        if (mandatoryUserAccount) setValue('create_account', true);
    }, [mandatoryUserAccount, setValue]);

    const validateEmail = (email: string) => Joi.string().email({ tlds: false }).validate(email).error === undefined;

    const handleForgotten = () =>
        addModal({
            title: t('Passwort zurücksetzen'),
            content: (id) => <Forgot callback={() => removeModal(id)} />,
            show: true,
            size: 40
        });

    const checkEmail = useCallback(() => {
        const email = getValues(nameMapping?.email ?? 'email');
        const password = getValues(nameMapping?.password ?? 'password');
        if (validateEmail(email)) {
            debouncedOnChange?.(email);
            setIsLoading(true);
            void api({
                url: 'login/guess',
                method: 'POST',
                data: { email, password },
                onBefore: () => setIsLoading(false),
                onSuccess: ({ name }) => {
                    setUserFound(true);
                    onUserFound?.(true);
                    if (name) setValue(nameMapping?.name ?? 'name', name);
                    if (!password) setFocus(nameMapping?.password ?? 'password');
                },
                onFailure: () => {
                    setUserFound(false);
                    onUserFound?.(false);
                },
                muted: true
            });
        }
    }, [getValues, document, nameMapping]);

    useEffect(checkEmail, []);

    const debouncedChangeHandler = useCallback(debounce(checkEmail, 500), []);

    // kiosk manual login: swap email + password for an access token and hand it up,
    // mirroring the QR AppLogin path so the member list and booking run authenticated.
    // returns true when a login was actually started, so the caller knows to wait for it
    const handleLogin = useCallback((): boolean => {
        const email = getValues(nameMapping?.email ?? 'email');
        const password = getValues(nameMapping?.password ?? 'password');
        // missing/invalid credentials: let the normal required-field validation surface the error
        if (!validateEmail(email) || !password) return false;
        setIsAuthenticating(true);
        setLoginError(null);
        void api({
            url: 'login',
            method: 'POST',
            data: { username: email, password },
            muted: true,
            onBefore: () => setIsAuthenticating(false),
            onSuccess: (data) => {
                if (data?.token) onAuthenticate?.(data.token, data.user);
                else setLoginError('Der Login war leider nicht erfolgreich.');
            },
            onFailure: () => setLoginError('Benutzername oder Passwort falsch.')
        });
        return true;
    }, [getValues, nameMapping, onAuthenticate, api]);

    // expose the login trigger to the parent and keep it in sync with the current closure
    useEffect(() => {
        registerAuthenticate?.(handleLogin);
        return () => registerAuthenticate?.(null);
    }, [registerAuthenticate, handleLogin]);

    // mirror the in-flight login state up so the parent can drive the "Weiter" button;
    // reset it on unmount so a successful login (which unmounts this form) can't strand the loader
    useEffect(() => {
        onAuthenticatingChange?.(isAuthenticating);
        return () => onAuthenticatingChange?.(false);
    }, [isAuthenticating, onAuthenticatingChange]);

    return !isAuthenticated ? (
        <>
            <FormInput
                contentRight={
                    isLoading ? <Spinner size="sm" color="currentColor" /> : <FontAwesomeIcon icon={faEnvelope} />
                }
                name={nameMapping?.email ?? 'email'}
                label="E-Mail Adresse *"
                onChange={debouncedChangeHandler}
            />

            {userFound ? (
                <Blockquote color="warning" className="tw-my-4">
                    Unter dieser E-Mail Adresse wurde bereits ein Sportision Account gefunden! Bitte gebe dein Passwort
                    ein oder klicke auf Passwort vergessen.
                    <Button block type="button" className="tw-mt-2" onClick={handleForgotten} size="xs" color="warning">
                        Passwort vergessen
                    </Button>
                </Blockquote>
            ) : (
                <>
                    <FormInput name={nameMapping?.name ?? 'name'} label="Name *" />
                    <FormInput.Checkbox
                        color="success"
                        name="create_account"
                        isDisabled={mandatoryUserAccount}
                        isSelected={mandatoryUserAccount || undefined}
                        label={
                            'Ich möchte einen Sportision Account erstellen' +
                            (mandatoryUserAccount ? ' (verplichtend)' : '')
                        }
                    />
                </>
            )}
            {(createAccount || userFound) && (
                <FormInput
                    type="password"
                    name={nameMapping?.password ?? 'password'}
                    label="Passwort *"
                    onChange={userFound && debouncedChangeHandler}
                />
            )}
            {onAuthenticate && userFound && loginError && (
                <Blockquote color="error" className="tw-my-2">
                    {loginError}
                </Blockquote>
            )}
            {!userFound && createAccount && (
                <FormInput
                    type="password"
                    name={nameMapping?.password_confirmation ?? 'password_confirmation'}
                    label="Passwort wiederholen *"
                />
            )}
        </>
    ) : (
        <></>
    );
};
